<template>
	<div class="date-block-wrapper container prevent-auto-margins">
		<div
		v-for="(groupBlock, index) in parsedByDate"
		class="group-wrapper"
		:key="`groupBlock-${index}`"
		:class="{'standalone': standAlone, 'mobile': getMobileAppState}">
			<!-- Date -->
			<divider-generic icon="calendar" :title="groupBlock.groupDate" :applyBorder="false" dividerSize="small"/>
			<!-- Table -->
			<table-generic
			class="table-container"
			:standAlone="false"
			:disabled="disabled"
			:noHoverStyles="noHoverStyles"
			:rowType="rowType"
			:headerType="headerType"
			:headerData="headerData"
			:hideHeader="hideHeader"
			:tableData="groupBlock.groupElements.map(obj => obj.rowData)"
			:tableStyles="tableStyles"
			:hasMore="hasMore && parsedByDate.length === index+1"
			:loadMoreText="loadMoreText"
			:loadingMoreText="loadingMoreText"
			:isLoadingMore="isLoadingMore"
			:alwaysBehaveLikeMobile="alwaysBehaveLikeMobile"
			@rowClicked="$emit('rowClicked', $event)"
			@moreOptionsClicked="$emit('moreOptionsClicked', $event)"
			@loadMore="$emit('loadMore')"
			@downloadClicked="emit('downloadClicked', $event)"
			@buttonClicked="emit('buttonClicked', $event)"/>
		</div>
	</div>
</template>

<script setup>
import TableGeneric from '@/components/generic/table/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
/* Assets */
import { tableTransactionDateByMonthParser, tableTransactionDateParser } from '@/assets/scripts/parsers/tables'
/* Vue */
import { computed } from 'vue'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
/* mapGetters */
const { getMobileAppState } = mapGetters()
/* Emmitters */
const emit = defineEmits(['rowClicked', 'moreOptionsClicked', 'loadMore', 'downloadClicked', 'buttonClicked'])

/** Props received:
@groupByMonth { Boolean } - prop to group elements by month, default behaviour is by day
@standAlone { Boolean } - prop to apply standAlone class, making our table have its own "box"
@disabled { Boolean } - prop to disabled clicks on table
@noHoverStyles { Boolean } - prop to remove hover styles
@rowType { String } - prop to define row type [generic(default)]
@headerType { String } - prop to define header type [generic(default)]
@headerData { Array } in which each Object is a column in your list with:
	- label [String]: text shown under the step
	- sortable [Boolean]: there are some tables (without pagination) that require a sortable option
	- dataAttr [String, Number]: identifier, used for sortable and to create groups, use (when available) prop provided by MDW for this column
	- showInMobile [Boolean, Object]:
		-[Boolean] if we receive a boolean your cell in mobile will be a column
		-[Object] if we receive an object, your cell will have multiple lines in one column
			key: groupWith, value: dataAttr we want to pair with this cell in mobile layout
@hideHeader {Boolean}
@tableData {Array} -
@tableStyles {Array} -
@hasMore {Boolean} -
@loadMoreText {String} -
@loadingMoreText {String} - Text to display while loading more
@isLoadingMore {Boolean} - prop to tell table when to show loader or button
@alwaysBehaveLikeMobile {Boolean} - prop to force mobile layout even in higher resolutions
*/
const props = defineProps({
	groupByMonth: { type: Boolean, default: () => false },
	standAlone: { type: Boolean, default: () => true },
	disabled: Boolean,
	noHoverStyles: Boolean,
	rowType: { type: String, default: () => 'generic' },
	headerType: { type: String, default: () => 'generic' },
	headerData: Array,
	hideHeader: Boolean,
	tableData: Array,
	tableStyles: Array,
	hasMore: Boolean,
	loadMoreText: String,
	loadingMoreText: String,
	isLoadingMore: Boolean,
	alwaysBehaveLikeMobile: Boolean
})

const parsedByDate = computed(() => {
	return props.groupByMonth ? tableTransactionDateByMonthParser(props.tableData) : tableTransactionDateParser(props.tableData)
})
</script>

<style lang="scss" scoped>
.date-block-wrapper {
	width: 100%;

	display: flex;
	flex-direction: column;
	align-items: center;
	flex-shrink: 0;

	.group-wrapper {
		width: 100%;
		padding: 16px 0 0 0;
		margin-bottom: 16px;

		display: flex;
		flex-direction: column;
		align-items: flex-start;
		flex-shrink: 0;

		.divider-generic-wrapper {
			text-transform: capitalize;
		}

		&.mobile {
			margin-bottom: 8px;

			.divider-generic-wrapper {
				width: calc(100% - 32px);
				margin: 0 16px;
			}
		}

		&.standalone {
			padding: 16px 0 0 0;

			@media only screen and (max-width: 1024px) {
				padding: 16px 0 0 0;
			}

			:deep(.table-bed) {
				padding: 16px 0 0 0;
				box-shadow: unset;
				border: none;

				&.mobile-style {
					padding-top: 0;

					.data-table-wrapper {
						.data-table-row {
							&:last-of-type {
								td:first-of-type { border-bottom-left-radius: 0 }
								td:last-of-type { border-bottom-right-radius: 0 }
							}
						}
					}
				}
			}
		}

		.divider-generic-wrapper :deep(.left-divider-title) {
			text-transform: capitalize;
		}

		&:last-of-type {
			margin-bottom: 0;
			padding-bottom: 0;
		}
	}
}
</style>
